import { makeAutoObservable, runInAction } from "mobx";
import { IDatabaseLineItem } from "../../LineItemDBCreationToolPage";
import SharedUtils from "../../../../shared/common/SharedUtils";
import {
	CollectionReference,
	DocumentData,
	Firestore,
	QueryDocumentSnapshot,
	collection,
	deleteDoc,
	doc,
	getFirestore,
	onSnapshot,
	setDoc,
} from "firebase/firestore";
import { logDebug, logError } from "../../../../shared/common/logger";
import { TemplateCellTuple } from "../../../../shared/common/Azure";
import { log } from "console";
import RestAPI from "../../../../utils/restAPI";
import { getMappingStore } from "../utils/MappingUtils";
import {
	AIMatchings,
	AIMatchingsMap,
	ComplementLineItemRulesMap,
	ComplementLineItemRulesMapNew,
	IComplementLineItemRule,
	IDatabaseMapping,
	VariableMapping,
	VariableMappingMap,
} from "../../../../shared/common/EstimateEngine/EstimateEngineTypes";
import EngineUtils from "../../../../shared/common/EngineUtils";

// TODO :: remove this and use the one from shared/interfaces.s.ts
export enum EQuantityTypes {
	NoQuantity = "No Quantity",
	QuantityAsMultiplierBadSpelling = "Quantity as Multuplier",
	QuantityAsMultiplier = "Quantity as Multiplier",
	QuantityAsDivider = "Quantity as Divider",
	QuantityAsValue = "Quantity as value",
	QauntityAsSubtract = "Quantity as subtract",
}
export function createLineItemFromMappingAndInput(
	lineItem: IDatabaseLineItem,
	mapping: VariableMapping[],
	inputValue: string[],
	inputNoFilter: [
		{
			word: string;
			confidence: number;
		}[],
		{
			word: string;
			confidence: number;
		}[]
	]
): string {
	mapping = mapping.filter((m) => !m.deleted);
	const ticValues = inputValue.join(" ").replace(/\s/g, " ").toLowerCase().split(" ");
	const variableFromTic = mapping.filter((m) => ticValues.includes(m.ticInput.toLowerCase())).map((m) => (m.variableValue ?? "").toLowerCase());
	const output = lineItem.elements
		.map((element) => {
			if (element.type === "text") return element.value;
			if (!element.acceptableValues || !element.acceptableValues.length) return element.value;

			const var1 = element.acceptableValues.filter((v) => variableFromTic.includes(v));
			if (!var1.length) {
				if (element.allowEmptyValue) {
					return "";
				}
				return element.defaultValue || element.acceptableValues[0];
			}
			return var1[0];
		})
		.join(" ");
	return output;
}

class MappingToolStore {
	get supportedTicTypes() {
		return ["Type1", "Type1_1", "water_mitigation", "exterior"];
	}

	private _lineItems: IDatabaseLineItem[] = [];
	public get allLineItems() {
		return this._lineItems;
	}
	private _tupleID: string = "";
	public get tupleID() {
		return this._tupleID;
	}
	// #region variablesMapping property
	private _variablesMapping: VariableMappingMap = {};
	get variablesMappingCount(): number {
		return Object.keys(this._variablesMapping).length;
	}
	getVariablesMapping(key: string): VariableMapping[] {
		return SharedUtils.clone(this._variablesMapping[key] || []); // TODO :: think of something better
	}
	setVariablesMapping(key: string, value: VariableMapping[]) {
		this._variablesMapping[key] = value;
		this._variablesMapping = { ...this._variablesMapping };
	}
	resetVariablesMapping() {
		this._variablesMapping = {};
	}
	// #endregion

	// #region aiMatchings property
	private _aiMatchings: { [key: string]: AIMatchings } = {};
	get aiMatchingsCount(): number {
		return Object.keys(this._aiMatchings).length;
	}
	getAIMatchings(key: string): AIMatchings {
		return this._aiMatchings[key];
	}
	setAIMatchings(key: string, value: AIMatchings, merge = false) {
		logDebug("setAIMatchings", key, value);
		const newValue = merge ? { ...this._aiMatchings[key], ...value } : value;
		this._aiMatchings = {
			...this._aiMatchings,
			[key]: { ...newValue },
		};
		logDebug("setAIMatchings", this._aiMatchings);
	}
	resetAIMatchings() {
		this._aiMatchings = {};
	}
	get aiMatchings(): AIMatchingsMap {
		return this._aiMatchings;
	}

	// #endregion

	// #region selectedLineItems property
	private _selectedLineItems: IDatabaseLineItem[] = [];
	get selectedLineItems(): IDatabaseLineItem[] {
		return this._selectedLineItems;
	}
	set selectedLineItems(value: IDatabaseLineItem[]) {
		this._selectedLineItems = value;
	}
	refreshSelectedLineItems() {
		this._selectedLineItems = this._lineItems.filter((lineItem) => this._selectedLineItems.find((li) => li.id === lineItem.id)!);
	}
	private _selectedComplementLineItems: IDatabaseLineItem[] = [];
	get selectedComplementLineItems(): IDatabaseLineItem[] {
		return this._selectedComplementLineItems;
	}
	set selectedComplementLineItems(value: IDatabaseLineItem[]) {
		this._selectedComplementLineItems = value;
	}
	private _complementLineProperties: ComplementLineItemRulesMapNew = {};

	public deleteComplementLineProperties(key: string, tabIndex: number) {
		if (this._complementLineProperties[key]) {
			this._complementLineProperties[key].splice(tabIndex, 1);
		}
	}
	public getComplementLineTabCount(key: string): number {
		const retLen = this._complementLineProperties[key]?.length || 1;
		logDebug("Ret", retLen);
		return retLen;
	}
	public getComplementLineProperties(key: string, tabIndex: number): IComplementLineItemRule {
		return this._complementLineProperties[key]?.[tabIndex] ?? {};
	}
	public resetComplementLineProperties(key: string) {
		delete this._complementLineProperties[key];
	}
	public setComplementLineProperties(key: string, tabIndex: number, value: IComplementLineItemRule) {
		const elementToSet = this._complementLineProperties[key];
		if (!elementToSet) {
			this._complementLineProperties[key] = [];
		}
		if (!this._complementLineProperties[key][tabIndex]) {
			this._complementLineProperties[key][tabIndex] = value;
		} else {
			this._complementLineProperties[key][tabIndex] = { ...this._complementLineProperties[key][tabIndex], ...value };
		}
	}
	// #endregion

	//#region wizardStep property
	private _wizardStep: number = 0;
	get wizardStep(): number {
		return this._wizardStep;
	}
	set wizardStep(value: number) {
		this._wizardStep = value;
	}
	//#endregion

	//#region ticType property
	private _ticType: string = "Type1";
	get ticType(): string {
		return this._ticType;
	}
	set ticType(value: string) {
		this._ticType = value;
	}
	//#endregion

	//#region overTuple property
	private _overTuple: [number, number] = [-1, -1];
	get overTuple(): [number, number] {
		return this._overTuple;
	}
	set overTuple(value: [number, number]) {
		this._overTuple = value;
	}
	//#endregion

	//#region selectedTuple property
	private _selectedTuple: [number, number] = [-1, -1];
	get selectedTuple(): [number, number] {
		return this._selectedTuple;
	}
	set selectedTuple(value: [number, number]) {
		this._selectedTuple = value;
	}
	//#endregion

	//#region dbMappings property
	private _dbMappings: IDatabaseMapping[] = [];
	get dbMappings(): IDatabaseMapping[] {
		return this._dbMappings;
	}
	//#endregion

	private _mappingNotes: string = "";
	get mappingNotes(): string {
		return this._mappingNotes;
	}
	set mappingNotes(value: string) {
		this._mappingNotes = value;
	}

	private _badScans: string[] = [];
	get badScans(): string[] {
		return this._badScans;
	}
	set badScans(value: string[]) {
		this._badScans = value;
	}
	private _allowedText: string[] = [];
	get allowedText(): string[] {
		return this._allowedText;
	}
	set allowedText(value: string[]) {
		this._allowedText = value;
	}

	private _minQuantity: { [s: string]: number } = {};
	getMinQuantity(key: string) {
		return this._minQuantity[key] ?? 0;
	}
	setMinQuantity(key: string, value: number) {
		this._minQuantity[key] = value;
	}
	private _maxQuantity: { [s: string]: number } = {};
	getMaxQuantity(key: string) {
		return this._maxQuantity[key] ?? 0;
	}
	setMaxQuantity(key: string, value: number) {
		this._maxQuantity[key] = value;
	}

	private _defaultQuantity = 0;
	get defaultQuantity() {
		return this._defaultQuantity;
	}
	set defaultQuantity(value: number) {
		this._defaultQuantity = value;
	}

	private _defaultMeasurement = "No Measurements";
	get defaultMeasurement() {
		return this._defaultMeasurement;
	}
	set defaultMeasurement(value: string) {
		this._defaultMeasurement = value;
	}

	// convert enum to array
	public readonly quantityTypes = Object.values(EQuantityTypes).filter((v) => v !== EQuantityTypes.QuantityAsMultiplierBadSpelling);

	public readonly measurements = ["No Measurements", ...EngineUtils.availableMeasurements];

	constructor() {
		makeAutoObservable(this);
		this._fetchMappings();
	}

	private _overrideDefaultValues: { [s: string]: string } = {};
	getOverrideDefaultValue(key: string) {
		return this._overrideDefaultValues[key] ?? "";
	}
	setOverrideDefaultValue(key: string, value: string) {
		this._overrideDefaultValues[key] = value;
	}

	setCurrentTupple(selectedTupple?: TemplateCellTuple) {
		this._variablesMapping = {};
		this._selectedLineItems = [];
		this._selectedComplementLineItems = [];
		this.mappingNotes = "";
		this._aiMatchings = {};
		// this._ticType = 'Tic sheet type 1';
		this._tupleID = "";
		this._complementLineProperties = {};
		if (selectedTupple) {
			// SharedUtils.assert(this.wizardStep === 1, 'wizardStep should be 1');
			runInAction(() => {
				this._tupleID = selectedTupple.meta!.id!;
				const mapping = this._dbMappings.find((dbMapping) => dbMapping.tupleID === this._tupleID);
				if (mapping) {
					this._variablesMapping = { ...mapping.variablesMapping };
					if (mapping.complementLineProperties && !mapping.complementLinePropertiesNew) {
						// old data from server. convert to new
						this._complementLineProperties = {};
						Object.entries(mapping.complementLineProperties).forEach(([key, value]) => {
							this._complementLineProperties[key] = [{ ...value }];
						});
					} else {
						this._complementLineProperties = { ...mapping.complementLinePropertiesNew };
					}

					this._selectedLineItems = mapping.selectedLineItemsID.map((lineItemID) => {
						return SharedUtils.clone(this._lineItems.find((lineItem) => lineItem.id === lineItemID)!);
					});
					this._selectedComplementLineItems =
						mapping.selectedComplementLineItemsID?.map((lineItemID) => {
							return SharedUtils.clone(this._lineItems.find((lineItem) => lineItem.id === lineItemID)!);
						}) ?? [];
					// this._aiMatchings = { ...mapping.aiMatchings };
					// alert(mapping.ticType)
					// this._ticType = mapping.ticType;
					this.mappingNotes = mapping.notes;
					this._variableDependency = mapping.variableDependency ?? [];
					this._badScans = mapping.badScans ?? [];
					this._allowedText = mapping.allowedText ?? [];
					this._defaultMeasurement = mapping.defaultMeasurement || "No Measurements";
					this._defaultQuantity = mapping.defaultQuantity || 0;
					this._minQuantity = mapping.minQuantity || {};
					this._maxQuantity = mapping.maxQuantity || {};
					this._overrideDefaultValues = mapping.overrideDefaultValues || {};
				}
				// logDebug('selectedItems from store', SharedUtils.clone(this._selectedLineItems));
			});
		}
	}
	public cloneCurrentMapping(state: "new" | "verified" | "unverified", addLineItems = false) {
		const newMapping: IDatabaseMapping = SharedUtils.clone({
			complementLinePropertiesNew: this._complementLineProperties,
			notes: this._mappingNotes,
			variablesMapping: this._variablesMapping,
			selectedLineItemsID: this._selectedLineItems.map((lineItem) => lineItem.id),
			selectedLineItems: addLineItems ? this._selectedLineItems : [],
			selectedComplementLineItemsID: this._selectedComplementLineItems?.map((lineItem) => lineItem.id) ?? [],
			selectedComplementLineItems: addLineItems ? this._selectedComplementLineItems ?? [] : [],
			tupleID: this._tupleID,
			state,
			aiMatchings: this._aiMatchings,
			ticType: this._ticType,
			variableDependency: this._variableDependency,
			badScans: this._badScans,
			allowedText: this._allowedText,
			defaultMeasurement: this._defaultMeasurement,
			defaultQuantity: this._defaultQuantity,
			minQuantity: this._minQuantity,
			maxQuantity: this._maxQuantity,
			overrideDefaultValues: this._overrideDefaultValues,
		} as IDatabaseMapping);
		return newMapping;
	}
	public async saveCurrentMapping(state: "new" | "verified" | "unverified") {
		const newMapping = this.cloneCurrentMapping(state);
		logDebug("saving mapping", newMapping);
		const firestore: Firestore = getFirestore();
		const docRef = doc(firestore, SharedUtils.getDbPath("tools/mapping_tool/mappings/" + newMapping.tupleID));
		getMappingStore().loadingData = true;
		try {
			await setDoc(
				docRef,
				{
					...newMapping,
				},
				{
					merge: false,
				}
			);
		} catch (err) {
			logError("Error while saving mapping", err);
		}
		getMappingStore().loadingData = false;
	}

	async getAllDocs(collection: CollectionReference): Promise<QueryDocumentSnapshot<DocumentData>[]> {
		return new Promise<any>((resolve, reject) => {
			const docs: QueryDocumentSnapshot<DocumentData>[] = [];
			try {
				const snapshot = onSnapshot(collection, (ss) => {
					ss.forEach((doc) => {
						docs.push(doc);
					});
					resolve(docs);
					snapshot();
				});
			} catch (err) {
				reject(err);
			}
		});
	}
	async copyCollection(source: string, destination: string) {
		// TODO: removed dev-utils endpoint — copy-collection no longer available
		console.warn("copyCollection is no longer available (dev-utils removed)");
	}

	public async deleteCurrentMapping() {
		const firestore: Firestore = getFirestore();
		const docRef = doc(firestore, SharedUtils.getDbPath("tools/mapping_tool/mappings/" + this._tupleID));
		const metaDataPath = SharedUtils.getDbPath(`tools/test/${this._tupleID}/metadata/inputData`);
		const openAIPath = SharedUtils.getDbPath(`tools/test/${this._tupleID}/metadata/openAIQueries`);
		const metaRef = collection(firestore, metaDataPath);
		const oaiRef = collection(firestore, openAIPath);
		try {
			await deleteDoc(docRef);
			await new Promise<void>((resolve, reject) => {
				// delete all the data from the metaDataPath
				try {
					const removeSnapshot = onSnapshot(metaRef, (snapshot) => {
						snapshot.forEach((doc) => {
							deleteDoc(doc.ref);
						});
						removeSnapshot();
						resolve();
					});
				} catch (err) {
					reject(err);
				}
			});

			await new Promise<void>((resolve, reject) => {
				// delete all the data from the openAIPath
				try {
					const removeSnapshot = onSnapshot(oaiRef, (snapshot) => {
						snapshot.forEach((doc) => {
							deleteDoc(doc.ref);
						});
						removeSnapshot();
						resolve();
					});
				} catch (err) {
					reject(err);
				}
			});
		} catch (err) {
			logError("Error while deleting mapping", err);
		}
	}

	private _variableDependency: ("dependent" | "inverted" | "independent")[] = [];
	public getVariableDependency(index: number) {
		return this._variableDependency.length > index ? this._variableDependency[index] : "independent";
	}
	public setVariableDependency(index: number, value: "dependent" | "inverted" | "independent") {
		if (this._variableDependency.length <= index) {
			this._variableDependency = [...this._variableDependency, ...Array(index - this._variableDependency.length + 1).fill("independent")];
		}
		this._variableDependency[index] = value;
	}

	public _fetchMappings() {
		const firestore: Firestore = getFirestore();
		SharedUtils.assert(firestore, "firebase is not initialized");
		const queryWatch = onSnapshot(collection(firestore, SharedUtils.getDbPath("tools/mapping_tool/mappings")), (snapshot) => {
			snapshot.docChanges().forEach((change) => {
				const docData = change.doc.data() as IDatabaseMapping;
				runInAction(() => {
					if (change.type === "added") {
						this._dbMappings.push(docData);
					} else if (change.type === "modified") {
						const index = this._dbMappings.findIndex((dbMapping) => dbMapping.tupleID === docData.tupleID);
						if (index !== -1) {
							this._dbMappings[index] = docData;
						}
					} else if (change.type === "removed") {
						const index = this._dbMappings.findIndex((dbMapping) => dbMapping.tupleID === docData.tupleID);
						if (index !== -1) {
							this._dbMappings.splice(index, 1);
						}
					}
				});
			});
			this._createCommonMappingsDB();
		});
		const queryWatch2 = onSnapshot(collection(firestore, SharedUtils.getDbPath("tools")), (snapshot) => {
			const lineItems: IDatabaseLineItem[] = [];
			snapshot.forEach((doc) => {
				runInAction(() => {
					const data = doc.data() as IDatabaseLineItem;
					lineItems.push({
						...data,
						id: doc.id,
					});
				});
			});
			runInAction(() => {
				this._lineItems = lineItems;
			});
		});
		return () => {
			queryWatch();
			queryWatch2();
		};
	}
	private _commonMappings: {
		[s: string]: {
			valueKey: string;
			value: string;
			count: number;
			quantityType?: string;
			quantityValue?: string;
			measurement?: string;
		}[];
	} = {};
	public get commonMappings() {
		return this._commonMappings;
	}
	private _createCommonMappingsDB() {
		this._commonMappings = {};
		this._dbMappings.forEach((dbMapping) => {
			const variablesMappingMap = dbMapping.variablesMapping;
			Object.values(variablesMappingMap).forEach((variablesMapping) => {
				variablesMapping.forEach((variableMapping) => {
					if (!variableMapping.deleted && variableMapping.accepted && variableMapping.ticInput && variableMapping.variableValue) {
						const keys = variableMapping.ticInput.toLowerCase();
						const value = variableMapping.variableValue?.toLowerCase().trim();
						const valueKey = `${value}::${variableMapping.quantityType ?? ""}::${variableMapping.quantityValue ?? ""}::${
							variableMapping.measurement ?? ""
						}`;
						if (!value) {
							return;
						}
						keys.split(",").forEach((key) => {
							key = key.trim();
							if (!this._commonMappings[key]) {
								this._commonMappings[key] = [];
							}
							const index = this._commonMappings[key].findIndex((v) => v.valueKey === valueKey);
							if (index !== -1) {
								this._commonMappings[key][index].count++;
							} else {
								const data: any = { valueKey, value, count: 1 };
								variableMapping.measurement && (data.measurement = variableMapping.measurement);
								variableMapping.quantityType && (data.quantityType = variableMapping.quantityType);
								variableMapping.quantityValue && (data.quantityValue = variableMapping.quantityValue);
								this._commonMappings[key].push(data);
							}
						});
					}
				});
			});
		});
		logDebug("commonMappings", SharedUtils.clone(this._commonMappings));
	}

	// const firestoreToolsCollectionRef = collection(useFirestore(), SharedUtils.getDbPath('tools')) as CollectionReference<IDatabaseLineItem>;
	// const lineItemsDataFromDataBase = useFirestoreCollectionData<IDatabaseLineItem>(firestoreToolsCollectionRef, { idField: 'id' });
}

const _mappingToolStore: MappingToolStore = new MappingToolStore();
const getMappingToolStore = () => _mappingToolStore;

export default getMappingToolStore;
